import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import { onUnmounted } from 'vue'
import App from './App.vue'
import { createSSRRouter, isClient } from './router'

// Import global styles
import './styles/index.css'

// Import environment types
import './vite-env.d.ts'

// Initialize runtime error handling for comprehensive browser error logging
import './utils/runtime-error-handler'

// Create the ViteSSG app with proper router configuration
export const createApp = ViteSSG(
  App,
  {
    routes: createSSRRouter().getRoutes(),
    base: import.meta.env.BASE_URL,
  },
  (ctx) => {
    // Install plugins
    const { app, router } = ctx
    app.use(createPinia())

    // Simplified client-side initialization
    if (isClient()) {
      // Ensure proper client-side routing
      if (router && typeof router.replace === 'function') {
        // Fix any initial routing issues
        const currentPath = window.location.pathname
        if (router.currentRoute.value.path !== currentPath) {
          router.replace(currentPath).catch(() => {
            // Ignore navigation errors during initialization
          })
        }
      }

      // Minimal metadata updates
      router.afterEach((to) => {
        if (to.meta.title) {
          document.title = to.meta.title as string
        }
      })

      // 🛡️ Initialize security system with enterprise features
      import('./stores/security').then(async ({ useSecurityStore }) => {
        try {
          const securityStore = useSecurityStore()
          await securityStore.initializeSecurity()

          // Provide security store globally
          ctx.app.provide('security', securityStore)

          console.log('🛡️ Security system initialized:', {
            status: securityStore.isInitialized ? 'Ready' : 'Loading',
            metrics: securityStore.metrics,
            overallStatus: securityStore.overallStatus
          })

          // Start real-time monitoring
          const monitoringInterval = setInterval(() => {
            securityStore.refreshMetrics()
          }, 5000)

          // Cleanup on unmount
          onUnmounted(() => {
            clearInterval(monitoringInterval)
          })

        } catch (error) {
          console.log('⚠️ Security system initialization failed:', error instanceof Error ? error.message : String(error))

          // Fallback to basic security monitoring
          setTimeout(() => {
            console.log('🛡️ Security system initialized (fallback mode)')
          }, 100)
        }
      })

      // 🧠 Initialize feature detection (Native WebRTC mode - no WASM dependencies)
      import('./utils/feature-detection').then(async ({ featureDetector }) => {
        try {
          // Detect platform capabilities first
          const features = await featureDetector.detectFeatures()
          console.log('📊 Platform features detected:', {
            performanceTier: features.performanceTier,
            crossOriginIsolated: features.platform.crossOriginIsolated,
            webPush: features.pwa.webPush,
          })

          // Provide features globally
          ctx.app.provide('features', features)

          console.log('✅ Native WebRTC mode initialized:', {
            performanceTier: features.performanceTier,
            note: 'Using browser WebRTC APIs - no WASM required'
          })

        } catch (error) {
          console.log('⚠️ Feature detection failed:', error instanceof Error ? error.message : String(error))

          // Fallback mode
          setTimeout(() => {
            console.log('✅ Native WebRTC initialized (fallback mode)')
          }, 100)
        }
      })

      // Service Worker registration
      if ('serviceWorker' in navigator && import.meta.env.PROD) {
        window.addEventListener('load', () => {
          navigator.serviceWorker.register('/sw.js').catch(() => {
            // Silent fail for SSG
          })
        })
      }
    }
  }
)